import { useState, useEffect } from 'react';
import logger from '../../lib/logger';
import AdminLayout from '../../components/layout/AdminLayout';
import { dashboardApi, eventosApi, participantesApi } from '../../lib/api';
import { Calendar, Users, CreditCard, TrendingUp, ArrowUpRight, ArrowDownRight, Target, Clock } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Progress } from '../../components/ui/progress';

const DashboardPage = () => {
  const [stats, setStats] = useState(null);
  const [recentParticipants, setRecentParticipants] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, participantsRes] = await Promise.all([
          dashboardApi.getStats(),
          participantesApi.list(),
        ]);
        
        setStats(statsRes.data);
        setRecentParticipants(participantsRes.data.slice(0, 5));
      } catch (error) {
        logger.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value || 0);
  };

  const getStatusBadge = (status) => {
    const styles = {
      pendente: 'badge-warning',
      aprovado: 'badge-success',
      cancelado: 'badge-error',
    };
    const labels = {
      pendente: 'Pendente',
      aprovado: 'Aprovado',
      cancelado: 'Cancelado',
    };
    return <span className={styles[status] || 'badge-warning'}>{labels[status] || status}</span>;
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="w-12 h-12 border-4 border-kairos-orange border-t-transparent rounded-full animate-spin"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-8" data-testid="dashboard-page">
        {/* Header */}
        <div>
          <h1 className="font-display text-3xl text-white mb-2">Dashboard</h1>
          <p className="text-gray-400">Visão geral do sistema Kairós</p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {/* Capacity Card - NEW */}
          {stats?.capacidade && (
            <Card className="bg-obsidian-200 border-kairos-orange/30 md:col-span-2 lg:col-span-4">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-gray-400">Capacidade do Evento</CardTitle>
                <Target className="w-5 h-5 text-kairos-orange" />
              </CardHeader>
              <CardContent>
                <div className="flex flex-col md:flex-row md:items-center gap-6">
                  <div className="flex-1">
                    <div className="flex items-baseline gap-2 mb-2">
                      <span className="text-4xl font-bold text-white">{stats.capacidade.ocupadas}</span>
                      <span className="text-gray-400">/ {stats.capacidade.max_vagas}</span>
                      <span className="text-gray-500 text-sm">vagas ocupadas</span>
                    </div>
                    <Progress 
                      value={(stats.capacidade.ocupadas / stats.capacidade.max_vagas) * 100} 
                      className="h-3 bg-gray-700"
                    />
                    <div className="flex justify-between mt-2 text-xs text-gray-500">
                      <span>{stats.capacidade.disponiveis} vagas disponíveis</span>
                      <span>{Math.round((stats.capacidade.ocupadas / stats.capacidade.max_vagas) * 100)}% ocupado</span>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-4 md:w-80">
                    <div className="text-center p-3 bg-green-500/10 rounded-lg border border-green-500/20">
                      <div className="text-2xl font-bold text-green-400">{stats.capacidade.aprovados}</div>
                      <div className="text-xs text-green-500/70">Confirmados</div>
                    </div>
                    <div className="text-center p-3 bg-yellow-500/10 rounded-lg border border-yellow-500/20">
                      <div className="text-2xl font-bold text-yellow-400">{stats.capacidade.pendentes_reserva}</div>
                      <div className="text-xs text-yellow-500/70 flex items-center justify-center gap-1">
                        <Clock className="w-3 h-3" />
                        Reservas
                      </div>
                    </div>
                    <div className="text-center p-3 bg-gray-500/10 rounded-lg border border-gray-500/20">
                      <div className="text-2xl font-bold text-gray-400">{stats.capacidade.pendentes_expirados}</div>
                      <div className="text-xs text-gray-500/70">Expirados</div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          <Card className="bg-obsidian-200 border-white/10">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-gray-400">Eventos Ativos</CardTitle>
              <Calendar className="w-5 h-5 text-kairos-orange" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-white">{stats?.eventos?.ativos || 0}</div>
              <p className="text-xs text-gray-500 mt-1">de {stats?.eventos?.total || 0} total</p>
            </CardContent>
          </Card>

          <Card className="bg-obsidian-200 border-white/10">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-gray-400">Participantes</CardTitle>
              <Users className="w-5 h-5 text-gray-400" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-white">{stats?.participantes?.total || 0}</div>
              <div className="flex items-center gap-2 mt-1">
                <span className="text-green-500 text-xs flex items-center">
                  <ArrowUpRight className="w-3 h-3" />
                  {stats?.participantes?.aprovados || 0} confirmados
                </span>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-obsidian-200 border-white/10">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-gray-400">Receita Total</CardTitle>
              <CreditCard className="w-5 h-5 text-green-500" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-white">{formatCurrency(stats?.receita_total)}</div>
              <p className="text-xs text-gray-500 mt-1">pagamentos aprovados</p>
            </CardContent>
          </Card>

          <Card className="bg-obsidian-200 border-white/10">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-gray-400">Pendentes</CardTitle>
              <TrendingUp className="w-5 h-5 text-yellow-500" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-white">{stats?.participantes?.pendentes || 0}</div>
              <p className="text-xs text-gray-500 mt-1">aguardando pagamento</p>
            </CardContent>
          </Card>
        </div>

        {/* Recent Participants */}
        <Card className="bg-obsidian-200 border-white/10">
          <CardHeader>
            <CardTitle className="text-white font-display">Participantes Recentes</CardTitle>
          </CardHeader>
          <CardContent>
            {recentParticipants.length === 0 ? (
              <p className="text-gray-500 text-center py-8">Nenhum participante registrado ainda.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="table-dark">
                  <thead>
                    <tr>
                      <th>Responsável</th>
                      <th>Cônjuge</th>
                      <th>E-mail</th>
                      <th>Cidade</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentParticipants.map((participant) => (
                      <tr key={participant.id}>
                        <td className="text-white font-medium">{participant.nome_responsavel}</td>
                        <td className="text-gray-400">{participant.nome_conjuge}</td>
                        <td className="text-gray-400">{participant.email}</td>
                        <td className="text-gray-400">{participant.cidade}</td>
                        <td>{getStatusBadge(participant.status_pagamento)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default DashboardPage;
